import React, { useEffect, useState } from "react";
import axios from "axios";
import { Pencil, Plus } from "lucide-react";

const ContactOfficeForm = ({ apiBase }) => {
  const [offices, setOffices] = useState([]);
  const [formData, setFormData] = useState({
    office_id: 0,
    office_title: "",
    office_description: "",
    office_hours: "",
    office_map: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [viewMode, setViewMode] = useState("table"); // "table" | "form"

  // ==============================
  // Fetch existing office details
  // ==============================
  const fetchOffices = async () => {
    try {
      setLoading(true);
      setError("");
      setOk("");

      const res = await axios.get(`${apiBase}/admin/office`);
      // contactController returns rows[0] from SP, which is an array
      const data = Array.isArray(res.data)
        ? res.data
        : [res.data].filter(Boolean);
      setOffices(data);
    } catch (e) {
      console.error("Fetch contact office failed:", e);
      setError(e?.response?.data?.error || "Failed to load office details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffices();
  }, [apiBase]);

  // ==============================
  // Handle input changes
  // ==============================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
  };

  // ==============================
  // Open form for NEW office
  // ==============================
  const handleAddNew = () => {
    setFormData({
      office_id: 0,
      office_title: "",
      office_description: "",
      office_hours: "",
      office_map: "",
    });
    setError("");
    setOk("");
    setViewMode("form");
  };

  // ==============================
  // Edit record
  // ==============================
  const handleEdit = (office) => {
    setFormData({
      office_id: office.office_id || 0,
      office_title: office.office_title || "",
      office_description: office.office_description || "",
      office_hours: office.office_hours || "",
      office_map: office.office_map || "",
    });
    setError("");
    setOk("");
    setViewMode("form");
  };

  // ==============================
  // Save (create or update)
  // ==============================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setOk("");

    if (!formData.office_title.trim()) {
      setSaving(false);
      setError("Office title is required");
      return;
    }

    try {
      await axios.post(`${apiBase}/admin/office/save`, {
        office_id: formData.office_id || 0, // 0 = create, >0 = update
        office_title: formData.office_title.trim(),
        office_description: formData.office_description?.trim() || null,
        office_hours: formData.office_hours?.trim() || null,
        office_map: formData.office_map?.trim() || null,
      });

      setOk(
        formData.office_id
          ? "Office details updated successfully"
          : "Office created successfully"
      );

      await fetchOffices();
      setViewMode("table");
    } catch (e) {
      console.error("Save contact office failed:", e);
      setError(e?.response?.data?.error || "Failed to save office details");
    } finally {
      setSaving(false);
    }
  };

  // ==============================
  // TABLE VIEW
  // ==============================
  if (viewMode === "table") {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-800">
            Office Locations
          </h2>
          <button
            onClick={handleAddNew}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
          >
            <Plus size={16} />
            Add Office
          </button>
        </div>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 text-red-700 px-4 py-2 text-sm mb-3">
            {error}
          </div>
        )}
        {ok && (
          <div className="rounded-md border border-green-200 bg-green-50 text-green-700 px-4 py-2 text-sm mb-3">
            {ok}
          </div>
        )}

        {loading ? (
          <div className="text-gray-600 text-sm">Loading office details...</div>
        ) : offices.length === 0 ? (
          <div className="text-gray-600 text-sm">
            No office records found yet. Click{" "}
            <span className="font-semibold">"Add Office"</span> to create one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-200 bg-white rounded-lg">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium border-b">
                    Title
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium border-b">
                    Description
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium border-b">
                    Hours
                  </th>
                  <th className="px-4 py-2 text-center text-sm font-medium border-b">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {offices.map((office) => (
                  <tr
                    key={office.office_id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-2 border-b text-gray-800">
                      {office.office_title}
                    </td>
                    <td className="px-4 py-2 border-b text-gray-600">
                      {office.office_description || "—"}
                    </td>
                    <td className="px-4 py-2 border-b text-gray-600">
                      {office.office_hours || "—"}
                    </td>
                    <td className="px-4 py-2 border-b text-center">
                      <button
                        onClick={() => handleEdit(office)}
                        className="inline-flex items-center justify-center text-blue-600 hover:text-blue-800"
                        title="Edit"
                      >
                        <Pencil size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // ==============================
  // FORM VIEW (NEW / EDIT)
  // ==============================
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-800">
        {formData.office_id ? "Edit Office" : "Add Office"}
      </h2>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-700 px-4 py-2 text-sm">
          {error}
        </div>
      )}
      {ok && (
        <div className="rounded-md border border-green-200 bg-green-50 text-green-700 px-4 py-2 text-sm">
          {ok}
        </div>
      )}

      <div className="grid grid-cols-1 gap-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Office Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="office_title"
            value={formData.office_title}
            onChange={handleChange}
            placeholder="Corporate Office - South India"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Office Description
          </label>
          <textarea
            name="office_description"
            value={formData.office_description}
            onChange={handleChange}
            rows={4}
            placeholder="Full address, landmark, directions, etc."
            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Office Hours
          </label>
          <textarea
            name="office_hours"
            value={formData.office_hours}
            onChange={handleChange}
            rows={3}
            placeholder="Mon–Sat: 9:30 AM – 6:30 PM"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Office Map (Google Maps URL or Embed)
          </label>
          <input
            type="text"
            name="office_map"
            value={formData.office_map}
            onChange={handleChange}
            placeholder="Paste Google Maps share or embed URL"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-5 py-2.5 text-white font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
        >
          {saving ? "Saving..." : "Save"}
        </button>
        <button
          type="button"
          onClick={() => setViewMode("table")}
          className="rounded-lg border border-gray-300 px-4 py-2.5 text-gray-700 hover:bg-gray-50"
        >
          Back to Table
        </button>
      </div>
    </form>
  );
};

export default ContactOfficeForm;
